import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';

import { RootState } from '@projects/Shell/store';
import * as LX from '@utils/lxWrapper';
import { getMessageConfig } from '@utils/messageConfig';
import { isHeadMultiPoi, getWmPoiId } from '@utils/ids';

import { reportUnifiedBusinessMessageNoticeRead } from '@projects/Shell/connection/push/pushMessageReceipt';

import { MessageNotification } from '../../store/models/pushNotification';
import { CustomizedShowArrType } from '../../api/define';

import styles from './WeakNotificationBox.module.scss';

const { useCallback, useState, useEffect, useRef } = React;

type WeakNotificationBoxProps = {
    totalCount: number;
    lastestNotifications: MessageNotification[];
    shouldShowNotification: boolean;
    clearNotification: () => void;
    clearSingleNotification: (notification: MessageNotification) => void;
};

const getButtonText = (notification: MessageNotification) => {
    return notification.href ? notification.linkText || '查看详情' : '我知道了';
};

/**
 *
 * 交互逻辑：
 * 当前第一条消息，默认展开消息，10s后收起
 * 非当前第一条消息，直接push到当前消息列表，展开/不展开全赖用户当前操作状态
 *
 */
const WeakNotificationBox: React.FC<WeakNotificationBoxProps> = ({
    totalCount,
    lastestNotifications,
    clearNotification,
    clearSingleNotification,
    shouldShowNotification,
}) => {
    const [showMsgList, setMsgListVisibility] = useState(false);
    const autoCloseMsgListTimerRef = useRef<number | null>(null);
    /** 是否展示非重要消息通知，可通过白名单或者消息提醒设置页配置 */
    const [messageConfig, setMessageConfig] = useState(true);

    /** 获取非重要消息配置 */
    useEffect(() => {
        setMessageConfig(getMessageConfig().weakNotification !== false);
    }, []);

    /** 切换全部消息列表展开收起状态 */
    const toggleMsgListVisibility = useCallback(() => {
        setMsgListVisibility(!showMsgList);

        // 用户交互操作，则中断默认的定时交互
        if (autoCloseMsgListTimerRef.current !== null) {
            clearTimeout(autoCloseMsgListTimerRef.current);
        }
    }, [setMsgListVisibility, showMsgList, autoCloseMsgListTimerRef.current]);

    const hideNotification = useCallback(
        (notification: MessageNotification) => {
            clearSingleNotification(notification);

            if (autoCloseMsgListTimerRef.current !== null) {
                clearTimeout(autoCloseMsgListTimerRef.current);
            }
        },
        [autoCloseMsgListTimerRef.current, clearSingleNotification],
    );

    useEffect(() => {
        // 首次收到通知，自动展开消息列表
        if (totalCount > 0 && autoCloseMsgListTimerRef.current === null) {
            setMsgListVisibility(true);
            autoCloseMsgListTimerRef.current = window.setTimeout(
                () => setMsgListVisibility(false),
                10 * 1000,
            );
        }
    }, [autoCloseMsgListTimerRef.current, totalCount, setMsgListVisibility]);

    const closeNotification = useCallback(
        (notification: MessageNotification) => {
            hideNotification(notification);
            reportUnifiedBusinessMessageNoticeRead(notification, {
                clickButtonText: '忽略',
                clickButtonType: 'CANCEL',
            });
        },
        [hideNotification],
    );

    /** 处理消息已读 */
    const readNotification = useCallback(
        (notification: MessageNotification) => {
            hideNotification(notification);

            if (notification.readReceiptConfig) {
                const config = notification.readReceiptConfig!;
                LX.reportMC(config.mc, config.cid);
            }

            reportUnifiedBusinessMessageNoticeRead(notification, {
                clickButtonText: getButtonText(notification),
                clickButtonType: 'CONFIRM',
            });

            LX.reportMC('b_waimai_e_a3egn8lk_mc', 'c_waimai_e_742h21bc', {
                poi_id: notification.wmPoiId || getWmPoiId(),
                msgId: notification.wmEPoiMsgId,
                traceId: notification.wmEMsgTraceId,
                pushType: 1,
            });

            if (notification.href) {
                window.location.hash = notification.href;
            }
        },
        [hideNotification],
    );

    if (totalCount === 0 || !shouldShowNotification || (isHeadMultiPoi() && !messageConfig)) {
        return null;
    }

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div>
                    全部 <span className={styles.msgCount}>{totalCount}</span> 条消息
                </div>
                <div>
                    <span className={styles.subText} onClick={clearNotification}>
                        全部清除
                    </span>
                    <span className={styles.subText}> | </span>
                    <span className={styles.subText} onClick={toggleMsgListVisibility}>
                        全部{showMsgList ? '收起' : '展开'}
                    </span>
                </div>
            </div>
            {showMsgList ? (
                <div className={styles.content}>
                    {lastestNotifications.map(notification => (
                        <VisibilitySensor
                            onChange={inViewport => {
                                if (inViewport) {
                                    LX.reportMV('b_waimai_e_a3egn8lk_mv', 'c_waimai_e_742h21bc', {
                                        poi_id: notification.wmPoiId || getWmPoiId(),
                                        msgId: notification.wmEPoiMsgId,
                                        traceId: notification.wmEMsgTraceId,
                                        pushType: 1,
                                    });
                                }
                            }}
                        >
                            <div key={notification.wmEPoiMsgId} className={styles.msgItem}>
                                <div className={styles.msgTitle}>{notification.title}</div>
                                <div className={styles.msgContent}>
                                    {notification.content}{' '}
                                    {notification.wmPoiName ? (
                                        <span className={styles.msgPoiName}>
                                            {notification.wmPoiName}
                                        </span>
                                    ) : null}
                                </div>
                                <div className={styles.msgOpe}>
                                    {notification.href ? (
                                        <span onClick={() => closeNotification(notification)}>
                                            忽略
                                        </span>
                                    ) : null}
                                    <span onClick={() => readNotification(notification)}>
                                        {getButtonText(notification)}
                                    </span>
                                </div>
                            </div>
                        </VisibilitySensor>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        shouldShowNotification: !state.shellCommon.customizedShowArr.includes(
            CustomizedShowArrType.PUSH_MESSAGE,
        ),
        totalCount: state.pushNotification.weakNotificationList.length,
        lastestNotifications: state.pushNotification.weakNotificationList.slice(0, 30),
    };
};

const mapDistachToProps = (dispatch: Dispatch) => {
    return {
        clearNotification: () => dispatch({ type: 'pushNotification/clearWeakNotification' }),
        clearSingleNotification: (notification: MessageNotification) =>
            dispatch({
                type: 'pushNotification/closeWeakNotification',
                payload: { wmEPoiMsgId: notification.wmEPoiMsgId },
            }),
    };
};

export default connect(mapStateToProps, mapDistachToProps)(WeakNotificationBox);
